import { ICinemaPalette, withAlpha } from '../palette';
import {
  BODY,
  DISP,
  getSharedTokens,
  GLASS_BORDER,
  INK,
  INK_DIM,
  INK_FAINT,
  HOVER,
  MOBILE,
  MONO,
  REDUCED_MOTION,
} from './styleTokens';

const PRESSED = { transform: 'translateY(0) scale(.98)' };

export const getLauncherStyles = (palette: ICinemaPalette) => {
  const { focusRing, dot, kicker } = getSharedTokens(palette);
  const sheen =
    'linear-gradient(180deg, rgba(255,255,255,.13), transparent 58%)';
  const button = {
    height: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '0 20px 0 16px',
    borderRadius: '12px',
    fontFamily: DISP,
    fontSize: '14.5px',
    fontWeight: 600,
    letterSpacing: '.01em',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition:
      'background .15s, border-color .15s, box-shadow .2s, transform .15s',
    '& > svg': { flex: 'none' },
    '&:focus-visible': { ...focusRing, outlineOffset: '3px' },
  };

  return {
    dock: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '12px',
      marginTop: '-12px',
      animation: 'mytaMiniGameFade .45s ease-out',
      [MOBILE]: { marginBottom: '10px' },
    },
    primary: {
      ...button,
      border: `1px solid ${withAlpha(palette.accentHi, 0.34)}`,
      background: `${sheen}, ${palette.buttonBackgroundColor}`,
      color: palette.ctaInk,
      boxShadow: `0 6px 22px ${withAlpha(
        palette.buttonBackgroundColor,
        0.3,
      )}, inset 0 1px 0 rgba(255,255,255,.16)`,
      '& > svg': { flex: 'none', width: '19px', height: '19px' },
      [HOVER]: {
        '&:hover': {
          background: `${sheen}, ${palette.buttonHoverColor}`,
          transform: 'translateY(-1px)',
          boxShadow: `0 10px 30px ${palette.accentGlow}, inset 0 1px 0 rgba(255,255,255,.18)`,
        },
      },
      '&:active': PRESSED,
    },
    primaryOpen: {
      boxShadow: `0 0 0 3px ${withAlpha(palette.accentHi, 0.2)}, 0 10px 30px ${
        palette.accentGlow
      }`,
    },
    ghost: {
      ...button,
      border: '1px solid rgba(255,255,255,.2)',
      background: 'rgba(8,10,15,.46)',
      backdropFilter: 'blur(14px) saturate(1.1)',
      WebkitBackdropFilter: 'blur(14px) saturate(1.1)',
      color: INK,
      boxShadow:
        '0 8px 24px rgba(4,6,9,.32), inset 0 1px 0 rgba(255,255,255,.07)',
      [HOVER]: {
        '&:hover': {
          background: 'rgba(255,255,255,.1)',
          borderColor: 'rgba(255,255,255,.34)',
          transform: 'translateY(-1px)',
        },
      },
      '&:active': PRESSED,
    },
    paper: {
      width: '392px',
      maxWidth: 'calc(100vw - 32px)',
      padding: '16px 10px 8px',
      borderRadius: '18px',
      border: GLASS_BORDER,
      backgroundColor: 'rgba(10,12,17,.8)',
      backgroundImage:
        'radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,.07), transparent 60%)',
      backdropFilter: 'blur(22px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(22px) saturate(1.2)',
      boxShadow:
        '0 24px 60px rgba(4,6,9,.55), inset 0 1px 0 rgba(255,255,255,.08)',
      color: INK,
      fontFamily: BODY,
    },
    paperUp: { marginTop: '-12px' },
    paperDown: { marginTop: '12px' },
    head: {
      padding: '0 10px 14px',
      marginBottom: '8px',
      borderBottom: '1px solid rgba(255,255,255,.08)',
    },
    kicker: { ...kicker, marginBottom: '9px' },
    status: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    dot: {
      ...dot,
      animation: 'mytaCinemaPulse 1.7s ease-in-out infinite',
      [REDUCED_MOTION]: { animation: 'none' },
    },
    eta: {
      flex: '1 1 auto',
      minWidth: 0,
      fontFamily: BODY,
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#fff',
    },
    pct: {
      flex: 'none',
      fontFamily: MONO,
      fontSize: '13px',
      fontWeight: 600,
      color: INK_DIM,
      fontVariantNumeric: 'tabular-nums',
    },
    list: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      outline: 'none',
    },
    item: {
      gap: '14px',
      padding: '10px',
      minHeight: 'auto',
      borderRadius: '14px',
      border: '1px solid transparent',
      alignItems: 'center',
      whiteSpace: 'normal',
      color: INK,
      fontFamily: BODY,
      transition: 'background .15s, border-color .15s',
      [MOBILE]: { gap: '12px' },
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: 'rgba(255,255,255,.07)',
        borderColor: 'rgba(255,255,255,.12)',
      },
      '&:hover .myta-mg-go, &.Mui-focusVisible .myta-mg-go': {
        opacity: 1,
        transform: 'translateX(0)',
      },
      '&:hover .myta-mg-tile, &.Mui-focusVisible .myta-mg-tile': {
        borderColor: withAlpha(palette.accentHi, 0.5),
        boxShadow: `0 0 0 1px ${withAlpha(
          palette.accentHi,
          0.18,
        )}, 0 8px 24px ${withAlpha(palette.buttonBackgroundColor, 0.35)}`,
      },
    },
    tile: {
      width: '58px',
      height: '58px',
      flex: 'none',
      display: 'grid',
      placeItems: 'center',
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,.14)',
      background:
        'radial-gradient(120% 90% at 50% 0%, rgba(255,255,255,.13), rgba(255,255,255,.02) 62%), rgba(6,8,12,.72)',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,.1), 0 6px 18px rgba(4,6,9,.4)',
      transition: 'border-color .2s, box-shadow .2s',
      [MOBILE]: { width: '50px', height: '50px', borderRadius: '13px' },
      '& .myta-mg-spark': {
        transformOrigin: '20px 20px',
        animation: 'mytaMiniGameOrbit 2.6s linear infinite',
        filter: `drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px ${palette.accentHi})`,
        [REDUCED_MOTION]: { animation: 'none' },
      },
      '& .myta-mg-chunk': {
        transformOrigin: '10px 15px',
        animation: 'mytaMiniGameChunk 3s cubic-bezier(.5,0,.8,.6) infinite',
        [REDUCED_MOTION]: { animation: 'none' },
      },
      '& .myta-mg-blade': {
        animation: 'mytaMiniGameBlade 2.8s cubic-bezier(.55,0,.25,1) infinite',
        filter: `drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px ${palette.accentHi})`,
        [REDUCED_MOTION]: { animation: 'none' },
      },
    },
    itemText: {
      flex: '1 1 auto',
      minWidth: 0,
    },
    itemTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '3px',
    },
    itemTitle: {
      fontFamily: DISP,
      fontSize: '15px',
      fontWeight: 600,
      letterSpacing: '-.005em',
      color: '#fff',
    },
    itemBadge: {
      fontFamily: MONO,
      fontSize: '9.5px',
      fontWeight: 600,
      letterSpacing: '.14em',
      textTransform: 'uppercase',
      lineHeight: 1,
      padding: '3px 6px 2px',
      borderRadius: '6px',
      color: palette.accentHi,
      border: `1px solid ${withAlpha(palette.accentHi, 0.45)}`,
      background: withAlpha(palette.accentHi, 0.08),
    },
    itemBlurb: {
      display: 'block',
      fontFamily: BODY,
      fontSize: '12.5px',
      lineHeight: 1.4,
      color: INK_DIM,
      textWrap: 'balance',
    },
    itemGo: {
      width: '18px',
      height: '18px',
      flex: 'none',
      color: INK_DIM,
      opacity: 0,
      transform: 'translateX(-4px)',
      transition: 'opacity .15s, transform .15s',
      [MOBILE]: { display: 'none' },
    },
    foot: {
      display: 'block',
      padding: '10px 10px 6px',
      fontFamily: BODY,
      fontSize: '11.5px',
      lineHeight: 1.45,
      color: INK_FAINT,
    },
  };
};
